export interface VideoStyle {
  id: string;
  nameKey: string;
  descriptionKey: string;
  enabled: boolean;
  badgeKey: string | null;
  estimated_time: string | null;
  thumbnail?: string;
}

export interface VideoTypeOption {
  id: string;
  nameKey: string;
  descriptionKey: string;
  badge: string;
  icon: "package" | "search";
  thumbnail?: string;
}

export const VIDEO_TYPE_OPTIONS: VideoTypeOption[] = [
  {
    id: "sassy-object",
    nameKey: "video-type-sassy-object-name",
    descriptionKey: "video-type-sassy-object-desc",
    badge: "",
    icon: "package",
    thumbnail: "https://fluxi.co/dev/assets/prod/assets/ads/creatives/templates/producto-con-vida.webp",
  },
  {
    id: "animated-problem",
    nameKey: "video-type-animated-problem-name",
    descriptionKey: "video-type-animated-problem-desc",
    badge: "",
    icon: "search",
    thumbnail: "https://fluxi.co/dev/assets/prod/assets/ads/creatives/templates/problema-animado.webp",
  },
];

export interface UGCAvatarConfig {
  gender: "female" | "male";
  age_range: "18-25" | "25-35" | "35-45" | "45+";
  skin_tone: "light" | "medium" | "tan" | "dark";
  hair: "short-straight" | "short-curly" | "long-straight" | "long-wavy" | "long-curly" | "bun" | "ponytail";
  hair_color: "black" | "brown" | "blonde" | "red" | "gray";
  vibe: "casual-natural" | "professional" | "sporty" | "glam";
  setting: "bedroom" | "bathroom" | "kitchen" | "gym" | "outdoor" | "office" | "studio-dark" | "studio-warm" | "home-office" | "outdoor-night" | "minimal" | "red-mood";
}

export interface VideoGenerationRequest {
  style_id: string;
  video_type_id: string;
  product_name: string;
  product_image: string;
  product_description: string;
  language: "es" | "en";
  duration: "5" | "10" | "15";
  user_prompt?: string;
  tone: "auto" | "educational" | "dramatic" | "humorous" | "urgent";
  angle?: string;
  category_override?: string | null;
  avatar_config?: UGCAvatarConfig | null;
}

export const VIDEO_STYLES: VideoStyle[] = [
  {
    id: "medical-3d",
    nameKey: "video-style-3d-anatomy",
    descriptionKey: "video-style-3d-anatomy-desc",
    enabled: true,
    badgeKey: "new",
    estimated_time: "8-10 min",
    thumbnail: "https://fluxi.co/dev/assets/prod/assets/ads/creatives/templates/animated-3d.webp",
  },
  {
    id: "ugc-testimonial",
    nameKey: "video-style-ugc-testimonial",
    descriptionKey: "video-style-ugc-testimonial-desc",
    enabled: true,
    badgeKey: "new",
    estimated_time: "8-10 min",
    thumbnail: "https://fluxi.s3.amazonaws.com/prod/assets/ads/creatives/templates/ugc-testimonial.webp",
  },
  {
    id: "product-modeling",
    nameKey: "video-style-modeling",
    descriptionKey: "video-style-modeling-desc",
    enabled: true,
    badgeKey: "new",
    estimated_time: "8-10 min",
    thumbnail: "https://fluxi.s3.amazonaws.com/prod/assets/ads/creatives/templates/modelando",
  },
  {
    id: "podcast-style",
    nameKey: "video-style-podcast",
    descriptionKey: "video-style-podcast-desc",
    enabled: true,
    badgeKey: "new",
    estimated_time: "8-10 min",
    thumbnail: "https://fluxi.s3.amazonaws.com/prod/assets/prod/assets/ads/creatives/templates/podcast.webp",
  },
  {
    id: "product-demo",
    nameKey: "video-style-product-demo",
    descriptionKey: "video-style-product-demo-desc",
    enabled: false,
    badgeKey: "coming-soon",
    estimated_time: null,
  },
];

export const TONE_OPTIONS = [
  { value: "auto" },
  { value: "educational" },
  { value: "dramatic" },
  { value: "humorous" },
  { value: "urgent" },
] as const;

export const DURATION_OPTIONS = [
  { value: "5", label: "5s", subtitleKey: "video-duration-5s-subtitle", credits: 50 },
  { value: "10", label: "10s", subtitleKey: "video-duration-10s-subtitle", credits: 75 },
  { value: "15", label: "15s", subtitleKey: "video-duration-15s-subtitle", credits: 97 },
] as const;

export const AVATAR_GENDER_OPTIONS = [
  { value: "female", labelKey: "gender-female" },
  { value: "male", labelKey: "gender-male" },
] as const;

export const AVATAR_AGE_OPTIONS = [
  { value: "18-25", labelKey: "age-18-25" },
  { value: "25-35", labelKey: "age-25-35" },
  { value: "35-45", labelKey: "age-35-45" },
  { value: "45+", labelKey: "age-45+" },
] as const;

export const AVATAR_SKIN_OPTIONS = [
  { value: "light", labelKey: "skin-light", color: "#FDEBD0" },
  { value: "medium", labelKey: "skin-medium", color: "#D4A574" },
  { value: "tan", labelKey: "skin-tan", color: "#B8860B" },
  { value: "dark", labelKey: "skin-dark", color: "#5C3317" },
] as const;

export const AVATAR_HAIR_OPTIONS = [
  { value: "short-straight", labelKey: "hair-short-straight" },
  { value: "short-curly", labelKey: "hair-short-curly" },
  { value: "long-straight", labelKey: "hair-long-straight" },
  { value: "long-wavy", labelKey: "hair-long-wavy" },
  { value: "long-curly", labelKey: "hair-long-curly" },
  { value: "bun", labelKey: "hair-bun" },
  { value: "ponytail", labelKey: "hair-ponytail" },
] as const;

export const AVATAR_HAIR_COLOR_OPTIONS = [
  { value: "black", labelKey: "hair-color-black", color: "#1a1a1a" },
  { value: "brown", labelKey: "hair-color-brown", color: "#6B3A2A" },
  { value: "blonde", labelKey: "hair-color-blonde", color: "#D4A76A" },
  { value: "red", labelKey: "hair-color-red", color: "#8B2500" },
  { value: "gray", labelKey: "hair-color-gray", color: "#A0A0A0" },
] as const;

export const AVATAR_VIBE_OPTIONS = [
  { value: "casual-natural", labelKey: "vibe-casual-natural" },
  { value: "professional", labelKey: "vibe-professional" },
  { value: "sporty", labelKey: "vibe-sporty" },
  { value: "glam", labelKey: "vibe-glam" },
] as const;

export const AVATAR_SETTING_OPTIONS = [
  { value: "bedroom", labelKey: "setting-bedroom" },
  { value: "bathroom", labelKey: "setting-bathroom" },
  { value: "kitchen", labelKey: "setting-kitchen" },
  { value: "gym", labelKey: "setting-gym" },
  { value: "outdoor", labelKey: "setting-outdoor" },
  { value: "office", labelKey: "setting-office" },
] as const;

export const PODCAST_SETTING_OPTIONS = [
  { value: "studio-dark", labelKey: "setting-studio-dark" },
  { value: "studio-warm", labelKey: "setting-studio-warm" },
  { value: "home-office", labelKey: "setting-home-office" },
  { value: "outdoor-night", labelKey: "setting-outdoor-night" },
  { value: "minimal", labelKey: "setting-minimal" },
  { value: "red-mood", labelKey: "setting-red-mood" },
] as const;

export const CATEGORY_OPTIONS = [
  { value: "", label: "Auto (IA clasifica)" },
  { value: "articulaciones", label: "Articulaciones" },
  { value: "digestivo", label: "Digestivo" },
  { value: "cardiovascular", label: "Cardiovascular" },
  { value: "piel-skincare", label: "Piel/Skincare" },
  { value: "cabello", label: "Cabello" },
  { value: "energia", label: "Energía" },
  { value: "sueno-estres", label: "Sueño/Estrés" },
  { value: "peso", label: "Peso" },
  { value: "dolor-inflamacion", label: "Dolor/Inflamación" },
  { value: "vision", label: "Visión" },
  { value: "inmunidad", label: "Inmunidad" },
  { value: "musculo-fitness", label: "Músculo/Fitness" },
  { value: "dental", label: "Dental" },
  { value: "higado-detox", label: "Hígado/Detox" },
  { value: "huesos", label: "Huesos" },
  { value: "cerebro-cognitivo", label: "Cerebro/Cognitivo" },
] as const;
